import React, { useState } from "react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Hammer, Tag, Award, Plus, Trash2 } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useGetFieldForceSkills } from "@features/wx/fieldForce/hooks";
import { useParams } from "react-router-dom";
import { AddSkillComponent } from "./addSkill";

interface SkillsTabProps {
  technicianId: string;
}

interface SkillData {
  id: number;
  skill: string;
  level: string;
  workOrdersCompleted: number;
  isPrimaryExpertise: boolean;
}

export const SkillsTab: React.FC<SkillsTabProps> = ({ technicianId }) => {
  const { id } = useParams();
  const { data: skillsData } = useGetFieldForceSkills(id);

  // Dialog state management - following the same pattern as CertificationsTab
  const [showAddSkillDialog, setShowAddSkillDialog] = useState(false);

  const handleAddNewSkill = () => {
    console.log("Opening add skill modal"); // Debug log
    setShowAddSkillDialog(true);
  };

  const handleCloseAddSkillModal = () => {
    console.log("Closing add skill modal"); // Debug log
    setShowAddSkillDialog(false);
  };

  const handleDeleteSkill = (skillId: number) => {
    // In a real app, this would delete the skill
  };

  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case "Expert":
        return "bg-green-100 text-green-800 border-green-200";
      case "Advanced":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const columns: TableColumn<SkillData>[] = [
    {
      key: "skill",
      header: "Skill",
      sortable: true,
    },
    {
      key: "createdDate",
      header: "Created Date",
      sortable: true,
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      sortable: true,
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="card-heading-text flex items-center">
          <Hammer className="mr-2 h-5 w-5 text-blue-500" />
          Skills & Expertise
        </h3>

        <Button
          onClick={handleAddNewSkill}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Skill
        </Button>
      </div>

      {skillsData?.length > 0 ? (
        <div className="mt-2">
          <GenericTable
            data={skillsData}
            columns={columns}
            rowKey="id"
            selectable={false}
            emptyMessage="No skills found"
          />
        </div>
      ) : (
        <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <Hammer className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium mb-2">No Skills Listed</h4>
          <p className="text-muted-foreground">
            This technician has no skills or expertise listed yet.
          </p>
        </div>
      )}

      {/* Add Skill Modal */}
      <AddSkillComponent
        isOpen={showAddSkillDialog}
        onClose={handleCloseAddSkillModal}
      />
    </div>
  );
};
